import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Divider,
  LinearProgress,
  Link,
  Typography,
} from '@weave-mui/material';

import LoadingSpinner from 'components/LoadingSpinner';
import ManageTestDataEditor from 'components/ManageTestDataModal/ManageTestDataEditor';
import WeaveConfirmationModal from 'components/WeaveConfirmationModal';
import WeaveMuiThemeProvider from 'components/WeaveMuiThemeProvider';

import { addNewNotification } from 'store/actions/notification_actions';
import { loadGlobalFields } from 'store/actions/hosting_actions';
import { transformTestData } from 'utils/transformTestDataUtils';
import {
  deleteHostingTestDataFile,
  getGlobalFields,
  getHostingProjectTestCases,
  getHostingProjects,
  postHostingCreateTestData,
  postHostingCreateTestDataFile,
} from 'utils/api.service';
import { uploadFilesToS3AndUpdatePayloadForNewTestDataFormat } from 'utils/manageTestDataOnboard';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * ManageTestDataPage
 *
 * Full-page experience for managing test data (same editor as ManageTestDataModal).
 * Selected test cases are provided either via router state (preferred) or query string:
 * - Router state: { selectedTestCaseIds: string[] }
 * - Query string: ?ids=<uuid>,<uuid>,...
 *
 * This page fetches:
 * - active project (for tools/envlist)
 * - test cases for that project
 * - global fields (for key validation/autocomplete inside editor)
 *
 * @returns {JSX.Element}
 */
function ManageTestDataPage() {
  const { category, projectname } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const menu = useSelector((state) => state.app.menu);
  const service = menu?.service;
  const workstream = service ? menu?.[service] : undefined;
  const workstreamName = menu?.categoryName;
  const org = service;

  const editorRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [testCases, setTestCases] = useState([]);
  const [shouldRefreshProject, setShouldRefreshProject] = useState(false);

  const [editorState, setEditorState] = useState({
    submitEnabled: false,
    loading: false,
    showActions: true,
  });

  const [showConfirmLeave, setShowConfirmLeave] = useState(false);

  const selectedIds = useMemo(() => {
    const stateIds = Array.isArray(location.state?.selectedTestCaseIds)
      ? location.state.selectedTestCaseIds
      : [];

    if (stateIds.length > 0) {
      return stateIds
        .filter((id) => typeof id === 'string' && UUID_REGEX.test(id))
        .filter((id, index, arr) => arr.indexOf(id) === index);
    }

    const params = new URLSearchParams(location.search);
    const idsParam = params.get('ids') || '';
    return idsParam
      .split(',')
      .map((s) => s.trim())
      .filter((id) => UUID_REGEX.test(id))
      .filter((id, index, arr) => arr.indexOf(id) === index);
  }, [location.search, location.state]);

  const normalizeTestCases = useCallback((rawTestCases, project) => {
    const envList = project?.envlist || [];
    return (rawTestCases || []).map((t) => {
      const tc = { ...t };
      tc.testdata = tc.testdata || {};

      const envObjExist = envList.some(
        (env) =>
          tc.testdata.hasOwnProperty(env.displayname.toUpperCase()) &&
          typeof tc.testdata[env.displayname.toUpperCase()] === 'object',
      );

      if (Object.keys(tc.testdata).length > 0 && !envObjExist) {
        const oldTestData = tc.testdata;
        tc.testdata = {};
        envList.forEach((e) => {
          tc.testdata[e.displayname.toUpperCase()] = oldTestData;
        });
      } else if (Object.keys(tc.testdata).length === 0) {
        envList.forEach((e) => {
          tc.testdata[e.displayname.toUpperCase()] = {};
        });
      }

      return tc;
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!org || !workstream || !projectname) {
        dispatch(
          addNewNotification(
            'Missing routing context for Manage Test Data',
            'danger',
          ),
        );
        history.replace(`/hosting/${category || ''}`);
        return;
      }

      if (!selectedIds.length) {
        dispatch(
          addNewNotification(
            'Select at least one test case to manage test data',
            'warning',
          ),
        );
        history.replace(`/hosting/${category || ''}`);
        return;
      }

      setIsLoading(true);
      try {
        const [projectsResponse, testCasesResponse, globalFieldsResponse] =
          await Promise.all([
            getHostingProjects(org, workstream),
            getHostingProjectTestCases(org, workstream, projectname),
            getGlobalFields(org, workstream),
          ]);

        const project =
          (projectsResponse.data.project || []).find(
            (p) => p.projectname === projectname,
          ) || null;

        if (!project) {
          dispatch(addNewNotification('Project not found', 'danger'));
          history.replace(`/hosting/${category || ''}`);
          return;
        }

        const normalized = normalizeTestCases(
          testCasesResponse.data.results || [],
          project,
        );

        if (isMounted) {
          setActiveProject(project);
          setTestCases(normalized);
          if (globalFieldsResponse?.data) {
            dispatch(loadGlobalFields(globalFieldsResponse.data));
          }
        }
      } catch (error) {
        dispatch(
          addNewNotification(
            'Failed to load test data editor context',
            'danger',
          ),
        );
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, [
    category,
    dispatch,
    history,
    org,
    normalizeTestCases,
    projectname,
    selectedIds.length,
    workstream,
  ]);

  const selectedTestCases = useMemo(() => {
    const set = new Set(selectedIds);
    return (testCases || []).filter((tc) => set.has(tc.id));
  }, [selectedIds, testCases]);

  const elements = useMemo(
    () => transformTestData(selectedTestCases),
    [selectedTestCases],
  );

  const handleSubmit = useCallback(
    async (payload, files) => {
      if (!activeProject) return null;

      setIsSubmitting(true);
      try {
        const { payload: updatedPayload, filesToRemoveFromS3 } =
          await uploadFilesToS3AndUpdatePayloadForNewTestDataFormat({
            payload,
            files,
            workstream,
            project: activeProject.projectname,
            originalTestCases: testCases,
            endpoint: postHostingCreateTestDataFile,
          });

        const response = await postHostingCreateTestData({
          projectname: activeProject.projectname,
          workstreamname: workstream,
          testcases: Object.values(updatedPayload),
        });

        // Best-effort cleanup: remove overwritten/cleared files from S3
        await Promise.all(
          (filesToRemoveFromS3 || []).map((uri) =>
            deleteHostingTestDataFile(uri).catch(() => null),
          ),
        );

        // Reload test cases (keeps page in sync after save)
        if (org && workstream) {
          const refreshed = await getHostingProjectTestCases(
            org,
            workstream,
            activeProject.projectname,
          );
          setTestCases(
            normalizeTestCases(refreshed.data.results || [], activeProject),
          );
        }
        setShouldRefreshProject(true);

        return response;
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeProject, normalizeTestCases, org, testCases, workstream],
  );

  const handleCancel = useCallback(() => {
    setShowConfirmLeave(true);
  }, []);

  const handleConfirmLeave = useCallback(() => {
    setShowConfirmLeave(false);
    const state =
      shouldRefreshProject && activeProject?.projectname
        ? { refreshProject: activeProject.projectname }
        : undefined;
    history.push(`/hosting/${category}`, state);
  }, [activeProject, category, history, shouldRefreshProject]);

  const handleCloseConfirmModal = useCallback(() => {
    setShowConfirmLeave(false);
  }, []);

  if (isLoading) {
    return (
      <WeaveMuiThemeProvider>
        <Box sx={{ p: 3 }}>
          <LoadingSpinner text="Loading test data editor..." />
        </Box>
      </WeaveMuiThemeProvider>
    );
  }

  return (
    <WeaveMuiThemeProvider>
      <Box
        sx={{
          p: 3,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          minWidth: 0,
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }} separator="/">
          <Link
            underline="hover"
            color="text.secondary"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              history.push('/');
            }}
          >
            Home
          </Link>
          <Link
            underline="hover"
            color="text.secondary"
            href={`/hosting/${category}`}
            onClick={(e) => {
              e.preventDefault();
              const state =
                shouldRefreshProject && activeProject?.projectname
                  ? { refreshProject: activeProject.projectname }
                  : undefined;
              history.push(`/hosting/${category}`, state);
            }}
          >
            Test hosting
          </Link>
          <Link
            underline="hover"
            color="text.secondary"
            href={`/hosting/${category}`}
            onClick={(e) => {
              e.preventDefault();
              const state =
                shouldRefreshProject && activeProject?.projectname
                  ? { refreshProject: activeProject.projectname }
                  : undefined;
              history.push(`/hosting/${category}`, state);
            }}
          >
            {workstreamName}
          </Link>
          <Typography color="text.primary">Manage test data</Typography>
        </Breadcrumbs>

        <Typography variant="h5" color="textPrimary" sx={{ mb: 0.5 }}>
          Manage test data
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          {activeProject?.displayname || activeProject?.projectname || ''}
        </Typography>

        {(isSubmitting || editorState.loading) && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 1,
            mr: -1,
            mb: 2,
          }}
        >
          <ManageTestDataEditor
            ref={editorRef}
            elements={elements}
            onSubmit={handleSubmit}
            title="Add test data"
            parent={activeProject}
            onStateChange={setEditorState}
          />
        </Box>

        {editorState.showActions !== false && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              py: 2.5,
              px: 3,
              mx: -3,
              mb: -3,
              mt: 3,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              paddingTop: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting || !editorState.submitEnabled}
              onClick={() => editorRef.current?.submit?.()}
            >
              Save
            </Button>
          </Box>
        )}

        <WeaveConfirmationModal
          open={showConfirmLeave}
          title="Leave page"
          message="Do you want to leave this page? Any unsaved changes will be lost."
          confirmButtonText="Leave"
          onConfirm={handleConfirmLeave}
          onClose={handleCloseConfirmModal}
          loading={false}
        />
      </Box>
    </WeaveMuiThemeProvider>
  );
}

export default ManageTestDataPage;
